import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Profiling/ProfilePage.css';
import './FeedbackPage.css';
import API_BASE_URL from '../config';
import PillFooter from '../LandingPage/components/PillFooter';

const CAREER_OPTIONS = [
  { value: 'employee', label: 'As an Employee (Job role)' },
  { value: 'entrepreneur', label: 'As an Entrepreneur (Start your own venture)' },
  { value: 'business_owner', label: 'As a Business Owner (Scale & build a brand)' },
  { value: 'still_exploring', label: 'Still exploring' },
];

const EXPECTATION_OPTIONS = [
  { value: 'yes_completely', label: 'Yes, completely' },
  { value: 'partially', label: 'Partially' },
  { value: 'not_really', label: 'Not really' },
];

const JOIN_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'maybe', label: 'Maybe' },
];

export default function FeedbackPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sessionRating, setSessionRating] = useState(0);
  const [careerPath, setCareerPath] = useState('');
  const [expectationsMet, setExpectationsMet] = useState('');
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [joinPrograms, setJoinPrograms] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nameTrim = name.trim();
    const emailTrim = email.trim();
    const phoneDigits = phone.replace(/\D/g, '');
    if (!nameTrim) {
      setAlertMessage('Please enter your name.');
      return;
    }
    if (!emailTrim) {
      setAlertMessage('Please enter your email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setAlertMessage('Please enter a valid email address.');
      return;
    }
    if (!phoneDigits) {
      setAlertMessage('Please enter your phone number (digits only).');
      return;
    }
    if (!sessionRating || sessionRating < 1 || sessionRating > 5) {
      setAlertMessage('Please rate today’s Open House session (1–5 stars).');
      return;
    }
    if (!careerPath) {
      setAlertMessage('Please select how you would like to build your career.');
      return;
    }
    if (!expectationsMet) {
      setAlertMessage('Please answer whether the session met your expectations.');
      return;
    }
    if (!joinPrograms) {
      setAlertMessage('Please indicate if you would like to be part of MEE School programs/community.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: nameTrim,
          email: emailTrim,
          phone: phoneDigits,
          sessionRating,
          careerPath,
          expectationsMet,
          additionalSuggestions: additionalFeedback.trim() || null,
          joinPrograms,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setAlertMessage('Thank you — your feedback has been submitted.');
        setName('');
        setEmail('');
        setPhone('');
        setSessionRating(0);
        setCareerPath('');
        setExpectationsMet('');
        setAdditionalFeedback('');
        setJoinPrograms('');
      } else {
        setAlertMessage(data?.error || 'Could not submit feedback. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setAlertMessage('Could not submit feedback. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="marketplace-container">
      {alertMessage && (
        <div className="profile-alert-overlay" onClick={() => setAlertMessage(null)}>
          <div className="profile-alert-box" onClick={(ev) => ev.stopPropagation()}>
            <p className="profile-alert-message">{alertMessage}</p>
            <button type="button" className="profile-alert-ok" onClick={() => setAlertMessage(null)}>
              OK
            </button>
          </div>
        </div>
      )}

      <header className="marketplace-header">
        <div className="logo-left">
          <img src="/p3.jpg" alt="Cinepreneur Logo" className="cinepreneur-logo" />
        </div>
        <div className="header-content">
          <h1 className="marketplace-title">CREATOR&apos;S ARCH</h1>
          <p className="marketplace-subtitle">EMPOWERING CREATORS, PROFESSIONALS</p>
        </div>
        <div className="logo-right">
          <img src="/p1.jpg" alt="MeeSchool Logo" className="meeschool-logo" />
        </div>
      </header>

      <div className="marketplace-body">
        <main className="main-content feedback-page-main">
          <h2 className="feedback-page-title">Feedback Form</h2>
          <p className="feedback-page-intro">
            Share your experience after the Open House. You do not need to log in to submit this form.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="feedback-question-block">
              <div className="feedback-question-title">Your contact details</div>
              <p className="feedback-question-hint">
                <span className="required-asterisk">*</span> Required
              </p>
              <div className="feedback-field-wrap">
                <label className="feedback-field-label" htmlFor="feedback-name">
                  Name <span className="required-asterisk">*</span>
                </label>
                <input
                  id="feedback-name"
                  type="text"
                  className="feedback-text-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  required
                />
              </div>
              <div className="feedback-field-wrap">
                <label className="feedback-field-label" htmlFor="feedback-email">
                  Email <span className="required-asterisk">*</span>
                </label>
                <input
                  id="feedback-email"
                  type="email"
                  className="feedback-text-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <div className="feedback-field-wrap">
                <label className="feedback-field-label" htmlFor="feedback-phone">
                  Phone number <span className="required-asterisk">*</span>
                </label>
                <input
                  id="feedback-phone"
                  type="tel"
                  className="feedback-text-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="tel"
                  placeholder="Digits only"
                  required
                />
              </div>
            </div>

            <div className="feedback-question-block">
              <div className="feedback-question-title">
                1. How would you rate today&apos;s Open House session? <span className="required-asterisk">*</span>
              </div>
              <p className="feedback-question-hint">1 = lowest, 5 = highest</p>
              <div className="feedback-stars" role="group" aria-label="Session rating 1 to 5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`feedback-star-btn ${sessionRating >= n ? 'active' : ''}`}
                    onClick={() => setSessionRating(n)}
                    aria-pressed={sessionRating === n}
                    aria-label={`${n} star${n > 1 ? 's' : ''}`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            <div className="feedback-question-block">
              <div className="feedback-question-title">
                2. How would you like to build your career in Media, Entertainment &amp; Entrepreneurship Industry?{' '}
                <span className="required-asterisk">*</span>
              </div>
              <div className="feedback-options">
                {CAREER_OPTIONS.map((opt) => (
                  <label key={opt.value} className="feedback-option-label">
                    <input
                      type="radio"
                      name="careerPath"
                      value={opt.value}
                      checked={careerPath === opt.value}
                      onChange={() => setCareerPath(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="feedback-question-block">
              <div className="feedback-question-title">
                3. Did the session meet your expectations? <span className="required-asterisk">*</span>
              </div>
              <div className="feedback-options">
                {EXPECTATION_OPTIONS.map((opt) => (
                  <label key={opt.value} className="feedback-option-label">
                    <input
                      type="radio"
                      name="expectationsMet"
                      value={opt.value}
                      checked={expectationsMet === opt.value}
                      onChange={() => setExpectationsMet(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="feedback-question-block">
              <div className="feedback-question-title">4. Any additional suggestions or feedback?</div>
              <textarea
                className="feedback-textarea"
                value={additionalFeedback}
                onChange={(e) => setAdditionalFeedback(e.target.value)}
                placeholder="Optional — your thoughts"
                rows={4}
              />
            </div>

            <div className="feedback-question-block">
              <div className="feedback-question-title">
                5. Would you like to be part of MEE School programs/community?{' '}
                <span className="required-asterisk">*</span>
              </div>
              <div className="feedback-options">
                {JOIN_OPTIONS.map((opt) => (
                  <label key={opt.value} className="feedback-option-label">
                    <input
                      type="radio"
                      name="joinPrograms"
                      value={opt.value}
                      checked={joinPrograms === opt.value}
                      onChange={() => setJoinPrograms(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <button type="submit" className="feedback-submit-btn" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit feedback'}
            </button>
          </form>

          <button type="button" className="feedback-back-link" onClick={() => navigate('/')}>
            ← Back to home
          </button>
        </main>
      </div>

      <PillFooter onNavigate={navigate} />
    </div>
  );
}
